import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Stack, Typography, TextField, MenuItem, Button, LinearProgress, Chip, IconButton, Tooltip,
  DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FlagIcon from '@mui/icons-material/OutlinedFlag';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import axios from 'axios';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from './firebaseConfig';
import { useNotify } from './components/NotificationProvider';
import AppDialog from './components/AppDialog';

// Brand palette (matches Ticket.js / Login redesign).
const C = {
  navy: '#0C447C', blue: '#185FA5', ghost: '#E6F1FB', pale: '#B5D4F4',
  amberBg: '#FAEEDA', amberBorder: '#FAC775', amberText: '#633806',
  greenBg: '#EAF3DE', greenText: '#27500A', border: '#D7E2EC',
  redBg: '#FDECEA', redBorder: '#F1B0AB', redText: '#B23A2E',
};

const FIELD_TRUCK_TYPES = [
  { value: 'BELLY', label: 'Belly' }, { value: 'END', label: 'End Dump' },
  { value: 'TANDEM', label: 'Tandem' }, { value: 'TRI', label: 'Tri Axle' },
  { value: 'SUPER', label: 'Super Dumps' }, { value: 'QUADS', label: 'Quads' },
  { value: 'FLAT', label: 'Flat Bed' }, { value: 'FLOAT', label: 'Float' },
];
const BASIS_OPTS = [{ value: 'HOUR', label: 'Hourly' }, { value: 'LOAD', label: 'Per load' }];

// Field schema per source. `money` fields are the ones the human is really here to confirm
// (quantity / truck / ticket #); they render with a subtle amber tint. `select` renders a dropdown.
const TON_FIELDS = [
  { key: 'date', label: 'Date' },
  { key: 'ticketNo', label: 'Ticket #', money: true },
  { key: 'vehicle', label: 'Truck #', money: true },
  { key: 'customerName', label: 'Customer' },
  { key: 'ton', label: 'Qty (tons)', money: true },
  { key: 'fsCode', label: 'FS / Job code' },
  { key: 'companyName', label: 'Plant / vendor' },
];
const FIELD_FIELDS = [
  { key: 'ticketNo', label: 'Ticket #', money: true },
  { key: 'truckNo', label: 'Truck #', money: true },
  { key: 'ticketDate', label: 'Date', type: 'date' },
  { key: 'billedTo', label: 'Billed To (customer)' },
  { key: 'truckType', label: 'Truck type', select: FIELD_TRUCK_TYPES },
  { key: 'basis', label: 'Basis', select: BASIS_OPTS },
  { key: 'quantity', label: 'Qty (hrs/loads)', money: true, type: 'number' },
  { key: 'producerLocation', label: 'From (producer)' },
  { key: 'destination', label: 'To (destination)' },
];

const fieldsFor = (item) => (item?.source === 'FIELD' ? FIELD_FIELDS : TON_FIELDS);

// Map API rows into a uniform queue item. Tonnage (InvoiceOcrDetails) and field (FieldTicketDTO)
// keep their own field names; the form schema above knows which set to render.
const mapTon = (d) => ({
  source: 'TON',
  key: `T-${d.ticketId}`,
  ticketId: d.ticketId,
  reviewStatus: d.reviewStatus,
  scanError: d.scanError,
  // A verified ticket whose code has no rate still invoices at $0 — surfaced here so the fix happens
  // in the verify loop instead of leaving a queue of verified-but-unbillable tickets behind.
  // NOTE: the backend computes hasRate with the SUB lookup (getRate(fsCode, date)), so it does NOT
  // tell you whether a PLANT rate exists. See the rate dialog.
  hasRate: d.hasRate,
  suggestedFsCode: d.suggestedFsCode,
  // The sub named in the ticket's Vehicle field ("1927 C & C TRANSPORT") — prefills Create-truck's
  // Company, so an unknown truck is one confirmation instead of a guess.
  truckCompany: d.truckCompany,
  imageUrl: d.documentId ? `/api/documents/${d.documentId}` : null,
  values: {
    date: d.date || '', ticketNo: d.ticketNo || '', vehicle: d.vehicle || '',
    customerName: d.customerName || '', ton: d.ton || '', fsCode: d.fsCode || '',
    companyName: d.companyName || '', jobCoe: d.jobCoe || '',
  },
});
const mapField = (ft) => ({
  source: 'FIELD',
  key: `F-${ft.id}`,
  fieldId: ft.id,
  reviewStatus: ft.reviewStatus,
  imageUrl: ft.hasImage ? `/api/field-tickets/${ft.id}/image` : null,
  values: {
    ticketNo: ft.ticketNo || '', truckNo: ft.truckNo || '', ticketDate: ft.ticketDate || '',
    billedTo: ft.billedTo || '', truckType: (ft.truckType || '').toUpperCase(),
    basis: (ft.basis || '').toUpperCase(), quantity: ft.quantity ?? '', loads: ft.loads ?? '',
    producerLocation: ft.producerLocation || '', destination: ft.destination || '',
    driver: ft.driver || '', foreman: ft.foreman || '',
  },
});

export default function VerifyWorkspace() {
  const apiUrl = process.env.REACT_APP_API_URL;
  const [user] = useAuthState(auth);
  const notify = useNotify();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(0);
  const [total, setTotal] = useState(0);         // scanned count when the batch loaded
  const [verified, setVerified] = useState(0);   // committed this session
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [form, setForm] = useState({});

  // Inline "create truck" (fix an unrecognized-truck ERROR ticket without leaving Verify).
  const [companies, setCompanies] = useState([]);
  const [truckOpen, setTruckOpen] = useState(false);
  const [truckForm, setTruckForm] = useState({ number: '', companyId: '', truckType: '' });
  const [addingCompany, setAddingCompany] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [savingCompany, setSavingCompany] = useState(false);
  const [creatingTruck, setCreatingTruck] = useState(false);

  // Inline "add rate" — a ticket with no rate for its code verifies fine and then invoices at $0.
  const [rateOpen, setRateOpen] = useState(false);
  const [rateForm, setRateForm] = useState({ fsCode: '', rate: '', effectiveDate: '', cardType: 'SUB' });
  const [savingRate, setSavingRate] = useState(false);

  const inputRefs = useRef([]);        // one ref per rendered field (for first-focus + F2 clear)
  const focusedKeyRef = useRef(null);  // key of the currently focused field
  const menuOpenRef = useRef(false);   // a Select menu is open (so Enter selects, not submit)

  const current = queue[index] || null;
  const fields = fieldsFor(current);
  const reviewAll = params.get('scope') === 'all'; // review-everything pass vs the flagged queue

  // ---- load queue ----
  // Two modes, same screen: the default FLAGGED queue (tonnage needing review ∪ pending field tickets),
  // or a REVIEW-ALL pass (?scope=all + the Tickets filters) that walks every ticket in the range so
  // silent misreads (e.g. an FS 4O read as 4Q) can be caught against the scan.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const reviewAll = params.get('scope') === 'all';
      let rows = [];
      if (reviewAll) {
        const p = {
          startDate: params.get('startDate') || '', endDate: params.get('endDate') || '',
          company: params.get('company') || '', truck: params.get('truck') || '',
          companyName: params.get('companyName') || '', plant: params.get('plant') || '',
        };
        const [tonRes, fieldRes] = await Promise.allSettled([
          axios.get(`${apiUrl}/api/invoice/ticket-details`, { params: p }),
          axios.get(`${apiUrl}/api/field-tickets`),
        ]);
        const tonRows = tonRes.status === 'fulfilled' ? (tonRes.value.data || []).map(mapTon) : [];
        // Field tickets have no server-side filter — narrow by the date range client-side; a plant
        // filter excludes them (field tickets have no plant).
        let fieldRaw = fieldRes.status === 'fulfilled' ? (fieldRes.value.data || []) : [];
        if (p.plant) fieldRaw = [];
        if (p.startDate) fieldRaw = fieldRaw.filter((ft) => !ft.ticketDate || ft.ticketDate >= p.startDate);
        if (p.endDate) fieldRaw = fieldRaw.filter((ft) => !ft.ticketDate || ft.ticketDate <= p.endDate);
        rows = [...tonRows, ...fieldRaw.map(mapField)];
      } else {
        // Flagged queue, scoped to the SAME filters as the Tickets grid that deep-linked here, so
        // "Verify flagged (N)" opens exactly those N (was the count/queue mismatch). review-queue now
        // honors startDate/endDate + company/truck/plant (backend applies the same AND-filters the grid
        // uses). Field tickets have no server-side filter, so mirror the grid's client-side narrowing
        // (Ticket.js refetch): date range, plant excludes them entirely, truck by vehicle, company by
        // the truck's owning company. Missing params = all-time (direct nav to /Verify).
        const startDate = params.get('startDate') || '';
        const endDate = params.get('endDate') || '';
        const company = params.get('company') || '';
        const truck = params.get('truck') || '';
        const plant = params.get('plant') || '';
        const [tonRes, fieldRes] = await Promise.allSettled([
          axios.get(`${apiUrl}/api/invoice/review-queue`, { params: { startDate, endDate, company, truck, plant } }),
          axios.get(`${apiUrl}/api/field-tickets`, { params: { status: 'PENDING' } }),
        ]);
        const tonRows = tonRes.status === 'fulfilled' ? (tonRes.value.data || []).map(mapTon) : [];
        let fieldRaw = fieldRes.status === 'fulfilled' ? (fieldRes.value.data || []) : [];
        if (startDate) fieldRaw = fieldRaw.filter((ft) => !ft.ticketDate || ft.ticketDate >= startDate);
        if (endDate) fieldRaw = fieldRaw.filter((ft) => !ft.ticketDate || ft.ticketDate <= endDate);
        if (plant) {
          fieldRaw = []; // field tickets have no plant — a plant filter excludes them (matches the grid)
        } else {
          if (truck) fieldRaw = fieldRaw.filter((ft) => String(ft.truckNo) === String(truck));
          if (company) {
            // company id → the owning company's truck numbers, via the SAME lookup the grid uses
            // (/api/invoice/lookups → truckNumberLookupList, fields companyId/truckNumber) so the
            // company→truck cascade is identical here and on the Tickets screen.
            let companyTruckNos = new Set();
            try {
              const tr = await axios.get(`${apiUrl}/api/invoice/lookups`, { params: { email: user?.email } });
              companyTruckNos = new Set(
                (tr.data?.truckNumberLookupList || [])
                  .filter((t) => String(t.companyId) === String(company))
                  .map((t) => String(t.truckNumber))
              );
            } catch { /* lookups unavailable → no company match, drop field tickets rather than over-include */ }
            fieldRaw = fieldRaw.filter((ft) => companyTruckNos.has(String(ft.truckNo)));
          }
        }
        rows = [...tonRows, ...fieldRaw.map(mapField)];
      }
      // "Review all" from the Tickets grid passes the EXACT set it's showing (Type/Status/Customer/
      // search already applied) as identity keys — restrict the queue to those so you review the
      // filtered subset, not every ticket in the date range. Lost on hard refresh (state-based), which
      // safely falls back to the full in-range review.
      const onlyKeys = location.state?.onlyKeys;
      if (Array.isArray(onlyKeys) && onlyKeys.length) {
        const keep = new Set(onlyKeys);
        rows = rows.filter((r) => keep.has(r.key));
      }
      setQueue(rows);
      setTotal(rows.length);
      setVerified(0);
      // Deep-link: position at ?ticket=<id> (tonnage) or ?field=<id> (field ticket) if present.
      const tid = params.get('ticket');
      const fid = params.get('field');
      let start = 0;
      if (tid) start = Math.max(0, rows.findIndex((r) => String(r.ticketId) === String(tid)));
      else if (fid) start = Math.max(0, rows.findIndex((r) => String(r.fieldId) === String(fid)));
      setIndex(start);
    } catch (e) {
      notify.error('Failed to load tickets.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, params, location.key]);

  useEffect(() => { load(); }, [load]);

  // Load the current item's values into the editable form; reset zoom.
  useEffect(() => {
    if (current) setForm({ ...current.values });
    setZoom(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, queue]);

  // Focus the first field whenever a new ticket is shown (auto-focus, spec §1.3 — mouse-free entry).
  useEffect(() => {
    if (!loading && current) {
      const t = setTimeout(() => inputRefs.current[0]?.focus(), 60);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [index, loading, current]);

  // Preload the NEXT ticket's scan image so there's no visible load between tickets (acceptance §2).
  useEffect(() => {
    const next = queue[index + 1];
    if (next?.imageUrl) {
      const img = new Image();
      img.src = `${apiUrl}${next.imageUrl}`;
    }
  }, [index, queue, apiUrl]);

  const setVal = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  // Companies for the inline create-truck dialog (a truck needs an owning company).
  useEffect(() => {
    axios.get(`${apiUrl}/api/master/companies`)
      .then((res) => setCompanies(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.debug('companies unavailable', err?.message));
  }, [apiUrl]);

  // ---- inline create-truck (fixes an unrecognized-truck row in place) ----
  // Loose match so OCR spelling doesn't defeat it: "C & C TRANSPORT" must find "C&C Transport".
  const squash = (s) => String(s ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  const matchCompanyId = (name) => {
    const n = squash(name);
    if (!n) return '';
    const exact = companies.find((c) => squash(c.name) === n);
    if (exact) return exact.id;
    // One name often contains the other ("PEREZ ENTERPRISES" vs "PEREZ ENTERPRISES TRANSPORTATION").
    const partial = companies.find((c) => {
      const cn = squash(c.name);
      return cn.length > 3 && n.length > 3 && (cn.includes(n) || n.includes(cn));
    });
    return partial ? partial.id : '';
  };

  const openCreateTruck = () => {
    // The ticket names the sub that owns the truck ("1927 C & C TRANSPORT"), so preselect it rather
    // than making the operator guess the company for every one of a packet's subs.
    setTruckForm({
      number: form.vehicle || '',
      companyId: matchCompanyId(current?.truckCompany),
      truckType: '',
    });
    setAddingCompany(false);
    // If the ticket named a company we don't have, offer it as the new-company name.
    setNewCompanyName(
      current?.truckCompany && !matchCompanyId(current.truckCompany) ? current.truckCompany : '',
    );
    setTruckOpen(true);
  };
  const addCompanyInline = async () => {
    const name = newCompanyName.trim();
    if (!name) { notify.warning('Enter a company name.'); return; }
    try {
      setSavingCompany(true);
      const res = await axios.post(`${apiUrl}/api/master/companies`, { name });
      const created = res.data;
      setCompanies((prev) => [...prev, created].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      setTruckForm((f) => ({ ...f, companyId: created.id }));
      setNewCompanyName('');
      setAddingCompany(false);
      notify.success(`Company "${created.name}" added.`);
    } catch (e) {
      notify.error(e?.response?.data?.message || e?.response?.data || 'Failed to add company');
    } finally {
      setSavingCompany(false);
    }
  };
  const createTruck = async () => {
    const number = String(truckForm.number || '').trim();
    if (!number || !truckForm.companyId) { notify.error('Truck number and company are required'); return; }
    try {
      setCreatingTruck(true);
      await axios.post(`${apiUrl}/api/master/trucks`, {
        number, companyId: truckForm.companyId,
        ...(truckForm.truckType ? { truckType: truckForm.truckType } : {}),
      });
      // Point the ticket at the new truck and clear the local error signal so Verify passes.
      setVal(current.source === 'FIELD' ? 'truckNo' : 'vehicle', number);
      // Clear the flag on EVERY queued ticket waiting on this same truck, not just this one — one
      // packet routinely has several tickets from the same sub (4 of 9 here are truck 0224), and
      // creating the truck fixes all of them at once. Matching on trailing digits so the ticket's
      // "0224" matches a truck entered as "224".
      const digits = (s) => String(s ?? '').replace(/\D/g, '').replace(/^0+/, '');
      const newTruck = digits(number);
      let alsoCleared = 0;
      setQueue((q) => q.map((it) => {
        if (it.key === current.key) return { ...it, scanError: null };
        const itTruck = digits(it.source === 'FIELD' ? it.values?.truckNo : it.values?.vehicle);
        if (it.scanError && itTruck && itTruck === newTruck) {
          alsoCleared += 1;
          return { ...it, scanError: null };
        }
        return it;
      }));
      setTruckOpen(false);
      notify.success(alsoCleared > 0
        ? `Truck ${number} created — also clears ${alsoCleared} more ticket(s) in this queue. Press Verify & next to save.`
        : `Truck ${number} created — press Verify & next to save.`);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data || 'Failed to create truck';
      // 409 "already exists" is not a failure — it PROVES the truck is there and the ticket's
      // "not recognized" flag is a stale snapshot from scan time. Telling the user the create failed
      // would leave them stuck: the flag can only clear via a save, and the create they just tried is
      // the thing they'd expect to clear it. So treat it as the answer and clear the flag.
      if (String(msg).toLowerCase().includes('already exists')) {
        const number = String(truckForm.number || '').trim();
        setVal(current.source === 'FIELD' ? 'truckNo' : 'vehicle', number);
        const digits = (s) => String(s ?? '').replace(/\D/g, '').replace(/^0+/, '');
        const target = digits(number);
        setQueue((q) => q.map((it) => {
          if (it.key === current.key) return { ...it, scanError: null };
          const itTruck = digits(it.source === 'FIELD' ? it.values?.truckNo : it.values?.vehicle);
          return it.scanError && itTruck && itTruck === target ? { ...it, scanError: null } : it;
        }));
        setTruckOpen(false);
        notify.success(`Truck ${number} already exists — this ticket's flag was out of date. `
          + 'Press Verify & next to save.');
      } else {
        notify.error(msg);
      }
    } finally {
      setCreatingTruck(false);
    }
  };

  // ---- inline add rate ----
  // Default the effective date to the TICKET's date, not today: these are historical tickets, and a
  // rate effective today would not apply to a ticket dated months ago (the lookup is effective-dated).
  const isoOf = (v) => {
    if (!v) return new Date().toISOString().split('T')[0];
    const s = String(v);
    const us = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);      // MM/DD/YYYY as returned by the tonnage API
    if (us) return `${us[3]}-${us[1]}-${us[2]}`;
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10); // already ISO (field tickets)
    const d = new Date(s);
    return isNaN(d.getTime()) ? new Date().toISOString().split('T')[0] : d.toISOString().split('T')[0];
  };
  const openAddRate = () => {
    setRateForm({
      fsCode: form.fsCode || '',
      rate: '',
      effectiveDate: isoOf(form.date),
      cardType: 'SUB',
    });
    setRateOpen(true);
  };
  const saveRate = async () => {
    const fsCode = String(rateForm.fsCode || '').trim();
    if (!fsCode) { notify.warning('Code is required.'); return; }
    if (rateForm.rate === '' || rateForm.rate == null || isNaN(Number(rateForm.rate))) {
      notify.warning('Enter a rate.'); return;
    }
    if (!rateForm.effectiveDate) { notify.warning('Effective date is required.'); return; }
    try {
      setSavingRate(true);
      await axios.post(`${apiUrl}/api/master/rate-cards`, {
        fsCode,
        rate: Number(rateForm.rate),
        effectiveDate: rateForm.effectiveDate,
        cardType: rateForm.cardType,
        truckType: 'ALL',
      });
      // hasRate is a SUB-only check, so only a SUB rate clears the flag. A PLANT rate is still the
      // right thing to add for a plant invoice — it just won't change this badge.
      if (rateForm.cardType === 'SUB') {
        setQueue((q) => q.map((it) => (it.key === current.key ? { ...it, hasRate: true } : it)));
      }
      setRateOpen(false);
      notify.success(`${rateForm.cardType} rate ${Number(rateForm.rate).toFixed(2)} added for ${fsCode}`
        + ` effective ${rateForm.effectiveDate}`);
    } catch (e) {
      // 409 = a rate already exists for this code + effective date + type.
      notify.error(e?.response?.data?.message || e?.response?.data || 'Failed to save rate');
    } finally {
      setSavingRate(false);
    }
  };

  // ---- advance / requeue ----
  const advance = useCallback(() => {
    setIndex((i) => i + 1);
  }, []);

  // Move the current item to the tail (flag / skip) and stay on the same index (which now shows
  // the next item). If it's already the last unresolved item, we simply fall off the end.
  const requeue = useCallback((reason) => {
    setQueue((q) => {
      if (index >= q.length) return q;
      const copy = [...q];
      const [item] = copy.splice(index, 1);
      copy.push({ ...item, flagged: reason });
      return copy;
    });
    // index stays the same → renders what shifted up into this slot.
    notify.info(reason === 'MANUAL' ? 'Flagged for manual review — moved to end.' : 'Skipped — moved to end.');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  // ---- save + verify current, then auto-advance ----
  const submit = useCallback(async () => {
    if (!current || saving) return;
    setSaving(true);
    try {
      if (current.source === 'FIELD') {
        const v = form;
        if (!v.billedTo || !v.truckType || !v.basis || !(v.quantity !== '' && v.quantity != null)) {
          notify.warning('Set Billed To, truck type, basis and quantity before verifying.');
          setSaving(false);
          return;
        }
        await axios.put(`${apiUrl}/api/field-tickets/${current.fieldId}`, { ...v, email: user?.email });
        await axios.post(`${apiUrl}/api/field-tickets/${current.fieldId}/verify`, null, { params: { email: user?.email } });
      } else {
        await axios.put(`${apiUrl}/api/invoice/update-ticket-details`, {
          ticketId: current.ticketId, ...form, email: user?.email,
        });
        await axios.put(
          `${apiUrl}/api/invoice/verify-ticket/${current.ticketId}?email=${encodeURIComponent(user?.email || '')}`);
      }
      setVerified((n) => n + 1);
      notify.success(`Ticket ${form.ticketNo || ''} verified`);
      advance();
    } catch (e) {
      notify.error(e?.response?.data?.message || e?.response?.data || 'Save/verify failed');
    } finally {
      setSaving(false);
    }
  }, [current, form, saving, apiUrl, user, notify, advance]);

  // Go back to the previous ticket (no save) — to re-check or fix one already passed.
  const goPrev = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);

  // ---- keyboard loop (spec §1.7) ----
  const onKeyDown = (e) => {
    // Let a Select's own menu handle its keys (Enter picks an option, etc.).
    if (menuOpenRef.current) return;

    const key = e.key;
    const code = e.code;
    // Enter / Numpad Enter, Ctrl+S, Numpad + → save & next.
    if ((key === 'Enter') || ((e.ctrlKey || e.metaKey) && (key === 's' || key === 'S'))
        || code === 'NumpadAdd' || (key === '+' && e.location === 3)) {
      e.preventDefault();
      submit();
      return;
    }
    // Esc / Ctrl+F → flag Incomplete/Unreadable, push to back of queue.
    if (key === 'Escape' || ((e.ctrlKey || e.metaKey) && (key === 'f' || key === 'F'))) {
      e.preventDefault();
      requeue('SKIP');
      return;
    }
    // F3 → flag for manual review (also pushed to the tail for the batch-end list).
    if (key === 'F3') {
      e.preventDefault();
      requeue('MANUAL');
      return;
    }
    // F2 → clear the currently focused field.
    if (key === 'F2') {
      e.preventDefault();
      const fk = focusedKeyRef.current;
      if (fk) setVal(fk, '');
      return;
    }
    // Alt+← → previous ticket (go back to re-check one, no save).
    if (e.altKey && key === 'ArrowLeft') {
      e.preventDefault();
      goPrev();
      return;
    }
    // Tab / Shift+Tab fall through to the browser's native field navigation (fields are in DOM order).
  };

  const done = !loading && (queue.length === 0 || index >= queue.length);
  const remaining = Math.max(0, queue.length - index);

  // What stops this ticket being billable, each with the fix that clears it. Rendered one per row —
  // a ticket routinely has BOTH (unknown truck AND no rate), and they used to be crammed into the
  // status row until it wrapped. Order matters: truck first, since verify is refused without it.
  const blockers = [];
  if (current && current.source !== 'FIELD') {
    if (current.scanError || current.reviewStatus === 'ERROR') {
      blockers.push({
        key: 'truck',
        label: current.scanError || 'Truck not recognized',
        action: 'Create truck',
        icon: <LocalShippingIcon sx={{ fontSize: 16 }} />,
        onClick: openCreateTruck,
        bg: C.redBg, border: C.redBorder, fg: C.redText,
      });
    }
    if (current.hasRate === false) {
      blockers.push({
        key: 'rate',
        label: `No rate${form.fsCode ? ` · ${form.fsCode}` : ''}`,
        action: 'Add rate',
        icon: <AddIcon sx={{ fontSize: 16 }} />,
        onClick: openAddRate,
        bg: C.amberBg, border: C.amberBorder, fg: C.amberText,
      });
    }
  }

  // ---- render ----
  return (
    <Box
      onKeyDown={onKeyDown}
      sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', outline: 'none' }}
      tabIndex={-1}
    >
      {/* header: progress + counter */}
      <Stack direction="row" alignItems="center" justifyContent="space-between"
        sx={{ px: 2, py: 1, borderBottom: `1px solid ${C.border}`, bgcolor: '#FAFBFC' }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Button
            size="small" startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
            onClick={() => navigate('/Ticket')}
            sx={{ textTransform: 'none', fontSize: 13, color: C.navy, minWidth: 0 }}
          >
            Tickets
          </Button>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: C.navy }}>
            {reviewAll ? 'Review' : 'Verify'}
          </Typography>
          <Chip size="small"
            label={reviewAll ? `Reviewing ${queue.length} ticket${queue.length === 1 ? '' : 's'}`
              : `${total} scanned → ${queue.length} need review`}
            sx={{ bgcolor: C.ghost, color: C.navy, fontWeight: 600 }} />
          {verified > 0 && (
            <Chip size="small" color="success" variant="outlined" label={`${verified} verified`} />
          )}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          {!done && current && (
            <Typography sx={{ fontSize: 13, color: '#6A7682' }}>
              Ticket {index + 1} of {queue.length} · {remaining} left
            </Typography>
          )}
          <Tooltip title="Close — back to Tickets">
            <IconButton size="small" aria-label="Close verify workspace"
              onClick={() => navigate('/Ticket')} sx={{ color: '#6A7682' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {loading && <LinearProgress />}

      {done ? (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
          <CheckCircleIcon sx={{ fontSize: 48, color: C.greenText }} />
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#17202A' }}>
            {total === 0 ? (reviewAll ? 'No tickets to review' : 'Nothing to verify')
              : (reviewAll ? 'Review complete' : 'Queue cleared')}
          </Typography>
          <Typography sx={{ fontSize: 14, color: '#6A7682' }}>
            {verified} ticket{verified === 1 ? '' : 's'} verified this session.
          </Typography>
          <Stack direction="row" spacing={1.5} sx={{ mt: 1 }}>
            <Button variant="outlined" onClick={load} sx={{ textTransform: 'none' }}>
              Reload queue
            </Button>
            <Button variant="contained" disableElevation startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/Ticket')} sx={{ textTransform: 'none', bgcolor: C.blue }}>
              Back to Tickets
            </Button>
          </Stack>
        </Box>
      ) : current && (
        <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
          {/* LEFT: scan image. Block/text-align (not flex) so a zoomed width > 100% is honored and the
              pane scrolls, instead of flex-shrink collapsing the image back to fit. */}
          <Box sx={{ flex: '1 1 55%', minWidth: 0, bgcolor: '#1A2129', position: 'relative', overflow: 'auto' }}>
            {current.imageUrl ? (
              <Box sx={{ textAlign: 'center', minHeight: '100%' }}>
                <img
                  src={`${apiUrl}${current.imageUrl}`}
                  alt="Scanned ticket"
                  style={{ width: `${zoom * 100}%`, maxWidth: 'none', height: 'auto', display: 'inline-block', verticalAlign: 'top' }}
                />
              </Box>
            ) : (
              <Typography sx={{ color: '#9AA6B2', display: 'block', textAlign: 'center', mt: 4 }}>
                No scan image stored for this ticket.
              </Typography>
            )}
            <Stack direction="row" spacing={0.5}
              sx={{ position: 'absolute', bottom: 12, right: 12, bgcolor: 'rgba(0,0,0,0.55)', borderRadius: 1, p: 0.25 }}>
              <Tooltip title="Zoom out"><span>
                <IconButton size="small" sx={{ color: '#fff' }} disabled={zoom <= 1}
                  onClick={() => setZoom((z) => Math.max(1, +(z - 0.25).toFixed(2)))}>
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
              </span></Tooltip>
              <Tooltip title="Zoom in"><span>
                <IconButton size="small" sx={{ color: '#fff' }} disabled={zoom >= 3}
                  onClick={() => setZoom((z) => Math.min(3, +(z + 0.25).toFixed(2)))}>
                  <ZoomInIcon fontSize="small" />
                </IconButton>
              </span></Tooltip>
            </Stack>
          </Box>

          {/* RIGHT: form */}
          <Box sx={{ flex: '1 1 45%', minWidth: 360, maxWidth: 560, display: 'flex', flexDirection: 'column',
            borderLeft: `1px solid ${C.border}` }}>
            <Box sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${C.border}` }}>
              {/* Row 1 — what this ticket IS. Status only, no actions, so it never grows. */}
              <Stack direction="row" alignItems="center" spacing={1}>
                <Chip size="small" label={current.source === 'FIELD' ? `Field · ${form.basis || '—'}` : 'Weigh'}
                  sx={{ bgcolor: C.ghost, color: C.navy, fontWeight: 600 }} />
                {current.flagged && (
                  <Chip size="small" icon={<FlagIcon sx={{ fontSize: 15 }} />}
                    label={current.flagged === 'MANUAL' ? 'Manual review' : 'Skipped'}
                    sx={{ bgcolor: C.amberBg, color: C.amberText }} />
                )}
              </Stack>

              {/* Rows 2+ — what BLOCKS billing, one per line: the problem on the left, its fix on the
                  right. Previously these were chips+buttons appended to the row above, which ran out
                  of width in this panel and wrapped "Create truck" / "Add rate" mid-phrase. One row
                  per blocker also scales: a ticket can be missing a truck AND a rate at once. */}
              {blockers.length > 0 && (
                <Stack spacing={0.75} sx={{ mt: 1.25 }}>
                  {blockers.map((b) => (
                    <Stack
                      key={b.key}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={1}
                      sx={{
                        bgcolor: b.bg, border: `1px solid ${b.border}`,
                        borderRadius: 1, pl: 1.25, pr: 0.75, py: 0.5,
                      }}
                    >
                      <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: b.fg, minWidth: 0,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={b.label}>
                        {b.label}
                      </Typography>
                      <Button
                        size="small"
                        startIcon={b.icon}
                        onClick={b.onClick}
                        sx={{ textTransform: 'none', fontSize: 12.5, fontWeight: 600, color: C.blue,
                          minWidth: 0, flexShrink: 0, whiteSpace: 'nowrap', py: 0 }}
                      >
                        {b.action}
                      </Button>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto', px: 2.5, py: 2 }}>
              <Stack spacing={2}>
                {fields.map((f, i) => {
                  const tint = f.money ? { bgcolor: C.amberBg } : undefined;
                  return (
                    <TextField
                      key={f.key}
                      inputRef={(el) => { inputRefs.current[i] = el; }}
                      label={f.label}
                      select={!!f.select}
                      type={f.type || 'text'}
                      value={form[f.key] ?? ''}
                      onChange={(e) => setVal(f.key, e.target.value)}
                      onFocus={() => { focusedKeyRef.current = f.key; }}
                      InputLabelProps={f.type === 'date' ? { shrink: true } : undefined}
                      size="small"
                      fullWidth
                      sx={{ '& .MuiOutlinedInput-root': tint }}
                      SelectProps={f.select ? {
                        onOpen: () => { menuOpenRef.current = true; },
                        onClose: () => { setTimeout(() => { menuOpenRef.current = false; }, 0); },
                      } : undefined}
                    >
                      {f.select && f.select.map((o) => (
                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                      ))}
                    </TextField>
                  );
                })}
              </Stack>
            </Box>

            {/* action bar */}
            <Stack direction="row" spacing={1} sx={{ px: 2.5, py: 1.5, borderTop: `1px solid ${C.border}` }}>
              <Tooltip title="Previous ticket (Alt+←)">
                <span>
                  <Button variant="outlined" color="inherit" onClick={goPrev} disabled={saving || index === 0}
                    sx={{ textTransform: 'none', whiteSpace: 'nowrap' }} startIcon={<ArrowBackIcon />}>
                    Prev
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="contained" color="success" disableElevation fullWidth
                startIcon={<CheckCircleIcon />} onClick={submit} disabled={saving}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Verify &amp; next
              </Button>
              <Tooltip title="Flag for manual review (F3)">
                <Button variant="outlined" onClick={() => requeue('MANUAL')} disabled={saving}
                  sx={{ textTransform: 'none', whiteSpace: 'nowrap' }} startIcon={<FlagIcon />}>
                  Flag
                </Button>
              </Tooltip>
              <Tooltip title="Skip — move to end of queue (Esc)">
                <Button variant="outlined" color="inherit" onClick={() => requeue('SKIP')} disabled={saving}
                  sx={{ textTransform: 'none' }} startIcon={<SkipNextIcon />}>
                  Skip
                </Button>
              </Tooltip>
            </Stack>

            {/* keyboard hint bar */}
            <Box sx={{ px: 2.5, py: 0.75, bgcolor: '#FAFBFC', borderTop: `1px solid ${C.border}` }}>
              <Typography sx={{ fontSize: 11.5, color: '#8A97A4' }}>
                <b>Tab</b> next field · <b>Enter</b>/<b>Ctrl+S</b> verify &amp; next · <b>Alt+←</b> previous ·
                {' '}<b>F2</b> clear field · <b>F3</b> flag · <b>Esc</b> skip
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* ---- inline create-truck dialog (fix an unrecognized-truck ticket without leaving Verify) ---- */}
      <AppDialog open={truckOpen} onClose={() => setTruckOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Create truck</DialogTitle>
        <DialogContent sx={{ mb: 1 }}>
          <Typography sx={{ fontSize: 13, color: '#6A7682', mb: 2 }}>
            Adds this truck to the master so the ticket resolves. Only do this for a genuinely new truck —
            if the number was just misread, edit the Truck # field instead. Creating it also clears every
            other ticket in this queue waiting on the same truck.
          </Typography>
          {/* The ticket names the truck's owner; show it, because a truck number is only unique
              within a company and the operator is the one deciding which company that is. */}
          {current?.truckCompany && (
            <Typography sx={{ fontSize: 12.5, mb: 2, px: 1.25, py: 0.75,
              bgcolor: C.ghost, color: C.navy, borderRadius: 1 }}>
              Ticket says this truck belongs to <b>{current.truckCompany}</b>
              {matchCompanyId(current.truckCompany)
                ? ' — company preselected below.'
                : ' — no matching company yet; add it below or pick one.'}
            </Typography>
          )}
          <TextField
            label="Truck Number" fullWidth required value={truckForm.number}
            onChange={(e) => setTruckForm((f) => ({ ...f, number: e.target.value }))}
            sx={{ mb: 2 }} size="small"
          />
          <TextField
            select label="Company" fullWidth required value={truckForm.companyId}
            onChange={(e) => setTruckForm((f) => ({ ...f, companyId: e.target.value }))}
            sx={{ mb: addingCompany ? 1 : 0.5 }} size="small"
          >
            {companies.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </TextField>
          {addingCompany ? (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 2 }}>
              <TextField
                label="New company name" size="small" fullWidth autoFocus value={newCompanyName}
                onChange={(e) => setNewCompanyName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCompanyInline(); } }}
              />
              <Button onClick={addCompanyInline} disabled={savingCompany} variant="contained" disableElevation>Add</Button>
              <Button onClick={() => { setAddingCompany(false); setNewCompanyName(''); }} color="secondary">Cancel</Button>
            </Box>
          ) : (
            <Button size="small" startIcon={<AddIcon />} onClick={() => setAddingCompany(true)}
              sx={{ textTransform: 'none', pl: 0, mb: 2, alignSelf: 'flex-start' }}>
              Add company
            </Button>
          )}
          <TextField
            select label="Truck Type (optional)" fullWidth value={truckForm.truckType}
            onChange={(e) => setTruckForm((f) => ({ ...f, truckType: e.target.value }))} size="small"
          >
            <MenuItem value="">—</MenuItem>
            <MenuItem value="BELLY">Belly Dump</MenuItem>
            <MenuItem value="END">End Dump</MenuItem>
            <MenuItem value="FLAT">Flatbed</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTruckOpen(false)} color="secondary">Cancel</Button>
          <Button onClick={createTruck} variant="contained" disableElevation disabled={creatingTruck}>Create truck</Button>
        </DialogActions>
      </AppDialog>

      {/* ---- inline add-rate dialog (a ticket with no rate verifies fine, then invoices at $0) ---- */}
      <AppDialog open={rateOpen} onClose={() => setRateOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Add rate</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense" label="FS / Job code" fullWidth required value={rateForm.fsCode}
            onChange={(e) => setRateForm((p) => ({ ...p, fsCode: e.target.value }))}
          />
          <TextField
            margin="dense" label="Rate ($ per ton)" type="number" fullWidth required
            value={rateForm.rate}
            inputProps={{ step: '0.01', min: '0' }}
            onChange={(e) => setRateForm((p) => ({ ...p, rate: e.target.value }))}
          />
          <TextField
            margin="dense" label="Effective date" type="date" fullWidth required
            value={rateForm.effectiveDate}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => setRateForm((p) => ({ ...p, effectiveDate: e.target.value }))}
            helperText="Defaults to this ticket's date. Rates are effective-dated — a rate dated after the ticket won't apply to it."
          />
          {/* SUB vs PLANT is not cosmetic: they are separate rate cards feeding different invoices,
              and picking the wrong one leaves the invoice at $0 with no visible error. */}
          <TextField
            margin="dense" select label="Rate type" fullWidth value={rateForm.cardType}
            onChange={(e) => setRateForm((p) => ({ ...p, cardType: e.target.value }))}
            helperText={rateForm.cardType === 'SUB'
              ? 'SUB — what the sub-contractor charges. Used by sub invoices + payroll. This is what the "No rate" badge checks.'
              : 'PLANT — what the plant pays. Used by the Plant Invoice. Adding this will NOT clear the "No rate" badge (that badge only checks SUB).'}
          >
            <MenuItem value="SUB">SUB — sub invoices &amp; payroll</MenuItem>
            <MenuItem value="PLANT">PLANT — plant invoices</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRateOpen(false)} color="secondary">Cancel</Button>
          <Button onClick={saveRate} variant="contained" disableElevation disabled={savingRate}>
            {savingRate ? 'Saving…' : 'Add rate'}
          </Button>
        </DialogActions>
      </AppDialog>
    </Box>
  );
}
